import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../Layout/LanguageContext';

// Logos liegen in public/images/kunden (zugeschnitten bzw. von den offiziellen Websites der Unternehmen).
// Ohne Logo wird der Name als Schriftzug gezeigt.
interface Kunde {
  name: string;
  logo?: string;
  mitName?: boolean; // Bildmarke ohne Schriftzug: Namen daneben anzeigen
}

const kunden: Kunde[] = [
  { name: 'Volkswagen', logo: '/images/kunden/volkswagen.png' },
  { name: 'Deutsche Bahn', logo: '/images/kunden/deutsche-bahn.png' },
  { name: 'PwC', logo: '/images/kunden/pwc.png' },
  { name: 'toom', logo: '/images/kunden/toom.png' },
  { name: 'Evident', logo: '/images/kunden/evident.svg' },
  { name: 'Hirschvogel', logo: '/images/kunden/hirschvogel.png', mitName: true },
  { name: 'STI Logistics', logo: '/images/kunden/sti-logistics.png' },
  { name: 'loyos', logo: '/images/loyos-bi.svg' },
  { name: 'Ideematec', logo: '/images/kunden/ideematec.png' },
  { name: 'Ritter Digital', logo: '/images/kunden/ritter-digital.png' },
  { name: 'b-imtec', logo: '/images/kunden/b-imtec.svg' }
];

const texte = {
  de: 'Unternehmen, für die ich gearbeitet habe',
  en: 'Companies I have worked for'
};

const laufen = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
`;

const Band = styled.section`
  background: var(--background-light);
  border-top: 1px solid var(--background-dark);
  border-bottom: 1px solid var(--background-dark);
  padding: 2rem 0 2.25rem;
  overflow: hidden;
`;

const Titel = styled.p`
  margin: 0 0 1.5rem;
  padding: 0 1rem;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-secondary);

  @media (max-width: 480px) {
    font-size: 0.72rem;
    letter-spacing: 0.08em;
  }
`;

const Fenster = styled.div`
  overflow: hidden;
  -webkit-mask-image: linear-gradient(to right, transparent, #000 6%, #000 94%, transparent);
  mask-image: linear-gradient(to right, transparent, #000 6%, #000 94%, transparent);

  @media (prefers-reduced-motion: reduce) {
    -webkit-mask-image: none;
    mask-image: none;
  }
`;

// Zwei identische Listen hintereinander: Nach einer halben Strecke beginnt die Animation von vorn, ohne Sprung.
const Spur = styled.div`
  display: flex;
  width: max-content;
  animation: ${laufen} 45s linear infinite;

  &:hover {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    width: auto;
    animation: none;

    ul + ul {
      display: none;
    }

    ul {
      flex-wrap: wrap;
      justify-content: center;
      row-gap: 1.5rem;
      max-width: 1100px;
      margin: 0 auto;
      padding: 0 1rem;
    }
  }
`;

const Liste = styled.ul`
  display: flex;
  align-items: center;
  gap: 4rem;
  margin: 0;
  padding: 0 4rem 0 0;
  list-style: none;

  @media (max-width: 768px) {
    gap: 2.75rem;
    padding-right: 2.75rem;
  }
`;

const Eintrag = styled.li`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  height: 48px;

  img {
    height: 40px;
    width: auto;
    max-width: 220px;
    object-fit: contain;
    filter: grayscale(1) contrast(1.3);
    opacity: 0.7;
    mix-blend-mode: multiply;
    transition: filter 0.3s ease, opacity 0.3s ease;
  }

  &:hover img {
    filter: none;
    opacity: 1;
  }

  @media (max-width: 768px) {
    height: 40px;

    img {
      height: 32px;
      max-width: 170px;
    }
  }
`;

const Schriftzug = styled.span`
  font-size: 1.3rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: var(--secondary-color);
  opacity: 0.7;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ClientLogos: React.FC = () => {
  const { language } = useLanguage();

  const liste = (kopie: boolean) => (
    <Liste aria-hidden={kopie || undefined}>
      {kunden.map((kunde) => (
        <Eintrag key={kunde.name}>
          {kunde.logo && <img src={kunde.logo} alt={kopie || kunde.mitName ? '' : kunde.name} />}
          {(!kunde.logo || kunde.mitName) && <Schriftzug>{kunde.name}</Schriftzug>}
        </Eintrag>
      ))}
    </Liste>
  );

  return (
    <Band aria-label={texte[language]}>
      <Titel>{texte[language]}</Titel>
      <Fenster>
        <Spur>
          {liste(false)}
          {liste(true)}
        </Spur>
      </Fenster>
    </Band>
  );
};

export default ClientLogos;
